import React from "react";
import '../SbtierCSS/SbtierServices.css';
import Typography from "@mui/material/Typography";
import useDocumentMeta from "../hooks/useDocumentMeta";

const fullStackStages = [
    {
        tag: "// DESIGN",
        title: "Design and Prototyping",
        text: "We take your idea from concept to a fully functional prototype, ensuring your chip design is optimized for performance and efficiency.",
    },
    {
        tag: "// VERIFICATION",
        title: "Testing and Verification",
        text: "With state-of-the-art tools and methodologies, we rigorously test every design, ensuring it meets stringent industry standards and passes all compliance checks.",
    },
    {
        tag: "// MANUFACTURING",
        title: "Manufacturing Support",
        text: "We assist you throughout the manufacturing process, ensuring smooth scaling from prototyping to mass production, with a focus on quality, yield, and cost.",
    },
];

const whyUsPoints = [
    {
        tag: "// TALENT",
        title: "Top Talent",
        text: "Our team consists of the best engineers in the semiconductor industry, each bringing a wealth of experience, creativity, and technical skill.",
    },
    {
        tag: "// CUSTOM",
        title: "Custom Solutions",
        text: "We don't believe in one-size-fits-all. Every project is unique, and we customize our services to ensure you get exactly what you need.",
    },
    {
        tag: "// INNOVATION",
        title: "Innovation at the Core",
        text: "With a relentless focus on innovation, we stay ahead of industry trends and incorporate the latest technologies into every project we undertake.",
    },
    {
        tag: "// RELIABILITY",
        title: "Reliability and Precision",
        text: "Our reputation is built on delivering reliable, high-quality solutions. We work meticulously to ensure every design is precise and production-ready.",
    },
];

function SbtierService(){
    useDocumentMeta(
        "Services",
        "Custom semiconductor design, SoC development, embedded systems, power optimization, and turnkey manufacturing support from SatyaBalTech Semiconductor."
    );

    return(
        <div className="sbtier-service-container">
            {/* Service Section */}
            <div className="sbtier-service-section" style={{backgroundImage:'url("/images/service_welcome_1.png")'}}>
                <div className="hero-overlay" />
                <div className="hero-inner">
                    <span className="hero-eyebrow">What We Deliver</span>
                    <Typography variant="h3" component="h3" className="service-title">
                        <span className="service-main">Our Services:</span> Powered by Engineering Excellence
                    </Typography>
                    <Typography variant="body1" className="service-text">
                        At SatyaBalTech semiconductor, we believe that the best technology is born from the best
                        talent. That’s why we’ve built a team of world-class engineers who are at the forefront of
                        semiconductor innovation. Our services are designed to meet the diverse needs of our
                        clients, leveraging the expertise of our engineers to deliver solutions that are
                        cutting-edge, efficient, and tailored to your specific requirements.
                    </Typography>
                </div>
            </div>
            {/** Custom Section*/}
            <div className="sbtier-services-custom-section">
                <div className="custom-content">
                <Typography variant="h3" component="h3" className="custom-title">
                    Custom Semiconductor Design
                </Typography>
                <Typography variant="body1" className="custom-text">
                    We specialize in custom semiconductor design, where our team of engineers works closely
                    with you to develop bespoke solutions that meet the unique demands of your application.
                    From concept to production, we handle every step of the design process, ensuring that your
                    product is optimized for performance, power efficiency, and scalability. Whether you need
                    specialized chips for AI, automotive, or IoT, we deliver high-performance solutions
                    engineered to the highest standards.
                </Typography>
                </div>
                <div className="custom-image">
                    <img src="images/service_custom.webp" className="sbtierServices-custom-image" alt="Custom semiconductor design"/>
                </div>
            </div>
            {/** Advance-soc System Section*/}
            <div className="sbtier-services-soc-section">
                <div className="soc-image">
                    <img src="images/service_advance.webp" className="sbtierServices-soc-image" alt="Advanced SoC development"/>
                </div>
                <div className="soc-content">
                <Typography variant="h3" component="h3" className="custom-title">
                    Advanced System-on-Chip (SoC) Development
                </Typography>
                <Typography variant="body1" className="soc-text">
                    Our engineers have extensive experience in designing and developing complex System-on-Chip
                    (SoC) solutions. We combine high-level integration with low-power design, delivering SoCs
                    that can power the most advanced technologies, from mobile devices and smart sensors to
                    automotive control systems and industrial automation. Our expertise covers everything from
                    architecture design and hardware integration to verification and validation, ensuring a
                    seamless and reliable solution for your product.
                </Typography>
                </div>
            </div>
            {/** Full stack Section*/}
            <div className="sbtier-services-solution-section">
                <Typography variant="h3" component="h3">
                    Full Stack Semiconductor Solutions
                </Typography>
                <Typography variant="body1" className="solution-text">
                    From early-stage design to post-production support, our full-stack service covers the entire
                    semiconductor lifecycle. Our engineers collaborate with your team to provide in-depth technical
                    insights and best practices at every stage:
                </Typography>
                <div className="solution-grid">
                    {fullStackStages.map((stage) => (
                        <div className="solution-card" key={stage.title}>
                            <span className="solution-card-tag">{stage.tag}</span>
                            <h4>{stage.title}</h4>
                            <p>{stage.text}</p>
                        </div>
                    ))}
                </div>
            </div>
            {/** Optimization-Embedded Section*/}
            <div className="optimization-embedded-container">
                <div className="sbtier-services-optimization-section">
                    <div className="optimization-content">
                        <Typography variant="h3" component="h3">
                            Power and Performance Optimization
                        </Typography>
                        <Typography variant="body1" className="optimization-text">
                            We understand the importance of balancing power consumption with performance, especially
                            in sectors like IoT, mobile devices, and automotive systems. Our engineers specialize in
                            optimizing your designs to reduce power draw while maintaining top performance, extending
                            battery life, reducing heat output, and improving overall system efficiency.
                        </Typography>
                    </div>
                </div>
                <div className="sbtier-services-embedded-section">
                    <div className="embedded-content">
                        <Typography variant="h3" component="h3">
                            Embedded Systems and Firmware Development
                        </Typography>
                        <Typography variant="body1" className="embedded-text">
                            Our team doesn’t just stop at hardware. We provide integrated embedded systems development,
                            offering complete hardware-software co-design services. Our engineers are adept at developing
                            custom firmware that ensures seamless communication and functionality between your hardware and software systems.
                            Whether it’s for smart sensors, embedded controllers, or other edge devices, we develop solutions that
                            are both reliable and efficient.
                        </Typography>
                    </div>
                </div>
            </div>
            {/** Consulting Section*/}
            <div className="sbtier-services-consulting-section">
                <div className="consulting-image">
                    <img src="images/service_consulting.webp" className="sbtierServices-Consulting-image" alt="Consulting and technical expertise"/>
                </div>
                <div className="consulting-content">
                <Typography variant="h3" component="h3">
                    Consulting and Technical Expertise
                </Typography>
                <Typography variant="body1" className="consulting-text">
                    Our engineers are available to provide strategic consulting for clients looking to explore new
                    semiconductor technologies or optimize their existing processes. Whether you need advice on
                    chip design, process improvements, or overcoming specific technical challenges, our expert team
                    can provide the guidance and expertise necessary to drive your project forward.
                </Typography>
                </div>
            </div>
            {/** Turnkey Section*/}
            <div className="sbtier-services-turnkey-section">
                <div className="turnkey-content">
                    <Typography variant="h3" component="h3">
                        Turnkey Solutions
                    </Typography>
                    <Typography variant="body1" className="turnkey-text">
                        For clients who need an all-in-one solution, we offer a comprehensive turnkey service. Our
                        engineers handle everything—from initial concept and design to testing, verification, and
                        manufacturing—so you can focus on your core business while we deliver a fully integrated solution
                        tailored to your needs.
                    </Typography>
                </div>
            </div>
            {/** Why-us Section*/}
            <div className="sbtier-services-whyUs-section">
                <Typography variant="h3" component="h3">
                    Why Choose Us?
                </Typography>
                <div className="why-us-grid">
                    <div className="why-us-cards">
                        {whyUsPoints.map((point) => (
                            <div className="why-us-card" key={point.title}>
                                <span className="why-us-card-tag">{point.tag}</span>
                                <h4>{point.title}</h4>
                                <p>{point.text}</p>
                            </div>
                        ))}
                    </div>
                    <div className="why-us-image">
                        <img src="images/service_why_us.webp" className="sbtierServices-WhyUs-image" alt="Why choose SatyaBalTech Semiconductor"/>
                    </div>
                </div>
            </div>
            <div className="sbtier-service-statement-section">
                <p className="service-statement">
                    At SatyaBalTech Semiconductor, we bring together the brightest minds to deliver semiconductor
                    solutions that are not only powerful but transformative. Let our team of expert engineers help
                    you bring your vision to life.
                </p>
            </div>
        </div>
    );
}

export default SbtierService;